<template>
  <div class="run-pc-page">
    <div class="app-card form-card">
      <el-form
        :model="form"
        :rules="formRules"
        ref="form"
        label-position="top"
        size="small"
      >
        <el-row :gutter="16">
          <el-col :span="8">
            <el-form-item :label="$t('text.product')" prop="productId">
              <el-select
                v-model="form.productId"
                :placeholder="$t('text.please_select')"
                filterable
                style="width:100%"
              >
                <el-option
                  v-for="item in products"
                  :key="item.id"
                  :label="item.productName"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-title">Organisations</div>
        <div
          v-for="(org, index) in form.organisations"
          :key="index"
          class="org-row"
        >
          <el-row :gutter="16" align="bottom" type="flex">
            <el-col :span="7">
              <el-form-item
                label="Organisation"
                :prop="'organisations.' + index + '.organisationId'"
                :rules="{
                  required: true,
                  message: 'Please select organisation',
                  trigger: 'change'
                }"
              >
                <el-select
                  v-model="org.organisationId"
                  :placeholder="$t('text.please_select')"
                  filterable
                  style="width:100%"
                >
                  <el-option
                    v-for="item in organisations"
                    :key="item.OrganisationId"
                    :label="item.OrganisationName"
                    :value="item.OrganisationId"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="9">
              <el-form-item
                label="Date Range"
                :prop="'organisations.' + index + '.dateRange'"
                :rules="{
                  required: true,
                  message: 'Please select date range',
                  trigger: 'blur'
                }"
              >
                <el-date-picker
                  v-model="org.dateRange"
                  format="dd/MM/yy HH:mm:ss"
                  value-format="yyyy-MM-dd HH:mm:ss"
                  type="datetimerange"
                  range-separator="-"
                  start-placeholder="Start Date"
                  end-placeholder="End Date"
                  :picker-options="pickerOptions"
                  style="width:100%"
                />
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item
                label="Offset"
                :prop="'organisations.' + index + '.offset'"
                :rules="{
                  required: true,
                  message: 'Please select offset',
                  trigger: 'change'
                }"
              >
                <el-select
                  v-model="org.offset"
                  :placeholder="$t('text.please_select')"
                  filterable
                  style="width:100%"
                >
                  <el-option
                    v-for="item in utcOffsetOptions"
                    :key="item.value"
                    :label="item.label"
                    :value="item.value"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="3" class="org-btn-col">
              <el-button
                v-if="index === 0"
                size="small"
                type="primary"
                plain
                icon="el-icon-plus"
                @click="addOrgItem()"
                >Add</el-button
              >
              <el-button
                v-else
                size="small"
                type="danger"
                plain
                icon="el-icon-minus"
                @click="removeOrgItem(index)"
                >Remove</el-button
              >
            </el-col>
          </el-row>
        </div>

        <div class="section-title">Parameters</div>
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item label="Abl" prop="abl">
              <el-input-number
                v-model="form.abl"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Min Percentage" prop="minPercentage">
              <el-input-number
                v-model="form.minPercentage"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Max Percentage" prop="maxPercentage">
              <el-input-number
                v-model="form.maxPercentage"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="16">
          <el-col :span="6">
            <el-form-item label="Sensor Epsilon" prop="sensorEpsilon">
              <el-input-number
                v-model="form.sensorEpsilon"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Sensor MinPoint" prop="sensorMinPoint">
              <el-input-number
                v-model="form.sensorMinPoint"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Volume Epsilon" prop="volumeEpsilon">
              <el-input-number
                v-model="form.volumeEpsilon"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
          <el-col :span="6">
            <el-form-item label="Volume MinPoint" prop="volumeMinPoint">
              <el-input-number
                v-model="form.volumeMinPoint"
                controls-position="right"
                style="width:100%"
              />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="form-actions">
          <el-button
            type="primary"
            @click="submitForm('form')"
            :loading="saveLoading"
            >RUN</el-button
          >
          <el-button @click="clear('form')">Clear</el-button>
        </div>
      </el-form>
    </div>

    <div v-show="result" class="app-card result-card">
      <div class="section-title">Result</div>
      <el-row :gutter="16" class="result-grid">
        <el-col :span="8" v-for="(val, key) in resultFields" :key="key">
          <div class="result-item">
            <span class="result-label">{{ key }}</span>
            <span class="result-value">{{ val }}</span>
          </div>
        </el-col>
      </el-row>
      <div id="sensorClusterChart" class="chart-area"></div>
      <div id="volumeClusterChart" class="chart-area"></div>
    </div>
  </div>
</template>

<script>
import ApiService from '@/services/CommonApiService.js'
import ReportApiService from '@/services/ReportApiService.js'
import swal from 'sweetalert'
import { utcOffsets } from '@/const'
export default {
  data() {
    return {
      utcOffsetOptions: utcOffsets,
      products: [],
      organisations: [],
      organisationsLoading: false,
      form: {
        productId: '',
        organisations: [
          {
            organisationId: '',
            offset: '',
            dateRange: []
          }
        ],
        abl: '',
        minPercentage: '',
        maxPercentage: '',
        sensorEpsilon: '',
        sensorMinPoint: '',
        volumeEpsilon: '',
        volumeMinPoint: ''
      },
      formRules: this.initFormRules(),
      saveLoading: false,
      pickerOptions: {
        disabledDate: time => {
          return time.getTime() > Date.now()
        }
      },
      result: null
    }
  },
  computed: {
    resultFields() {
      if (!this.result) return {}
      return {
        Slope: this.result.Slope,
        Intercept: this.result.Intercept,
        Equation: this.result.Equation,
        MedianLowSensorValue: this.result.MedianLowSensorValue,
        MedianHighSensorValue: this.result.MedianHighSensorValue,
        MedianVolume: this.result.MedianVolume
      }
    }
  },
  mounted() {
    this.getProducts()
    this.getOrganisations()
  },
  methods: {
    initFormRules() {
      return {
        productId: [
          {
            required: true,
            message: this.$t('text.product_required'),
            trigger: 'change'
          }
        ]
      }
    },
    getProducts() {
      const user = JSON.parse(localStorage.getItem('user'))
      ApiService.GetProductsById(user.id).then(response => {
        this.products = response.data.data
      })
    },
    getOrganisations() {
      this.organisationsLoading = true
      ReportApiService.GetAllOrganisations(this.filter)
        .then(response => {
          this.organisationsLoading = false
          if (response.data.success) {
            let organisations = response.data.data
            this.organisations = []
            organisations.forEach(item => {
              item.Organisations.forEach(org => {
                this.organisations.push(org)
              })
            })
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: this.$t('text.failed'),
              text: response.data.msg
            })
          }
        })
        .catch(() => {
          this.organisationsLoading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: this.$t('text.failed')
          })
        })
    },
    addOrgItem() {
      this.form.organisations.push({
        organisationId: '',
        offset: '',
        dateRange: []
      })
    },
    removeOrgItem(index) {
      this.form.organisations.splice(index, 1)
    },
    clear(formName) {
      this.$refs[formName].resetFields()
      this.result = null
    },
    submitForm(formName) {
      this.$refs[formName].validate(valid => {
        if (valid) {
          if (this.hasDuplicateOrganisationId()) {
            swal({
              icon: 'warning',
              title: 'Warning',
              text: 'Duplicate organisations cannot be added'
            })
          } else {
            this.handleSubmit()
          }
        }
      })
    },
    hasDuplicateOrganisationId() {
      let ids = new Set()
      for (let org of this.form.organisations) {
        if (ids.has(org.organisationId)) {
          return true
        }
        ids.add(org.organisationId)
      }
      return false
    },
    handleSubmit() {
      this.saveLoading = true
      let params = {
        ProductId: this.form.productId,
        Abl: this.form.abl,
        MinPercentage: this.form.minPercentage,
        MaxPercentage: this.form.maxPercentage,
        SensorEpsilon: this.form.sensorEpsilon,
        SensorMinPoint: this.form.sensorMinPoint,
        VolumeEpsilon: this.form.volumeEpsilon,
        VolumeMinPoint: this.form.volumeMinPoint,
        OrganisationDate: this.form.organisations.map(org => {
          return {
            OrganisationId: org.organisationId,
            OffSet: org.offset,
            FromDate: org.dateRange[0],
            ToDate: org.dateRange[1]
          }
        })
      }
      ReportApiService.GetAllCharacterisationData(params)
        .then(response => {
          this.saveLoading = false
          if (response.data.success) {
            this.result = response.data.data
            if (this.result.SensorCluster) {
              let sensorClusterData = this.result.SensorCluster.map(item => {
                return [item.Value, item.ClusterNumber]
              })
              this.initChart(
                'sensorClusterChart',
                sensorClusterData,
                'Sensor Cluster'
              )
            }
            if (this.result.VolumeCluster) {
              let volumeClusterData = this.result.VolumeCluster.map(item => {
                return [item.Value, item.ClusterNumber]
              })
              this.initChart(
                'volumeClusterChart',
                volumeClusterData,
                'Volume Cluster'
              )
            }
          } else {
            swal({
              icon: 'error',
              timer: 2000,
              title: 'Failed',
              text: response.data.msg
            })
          }
        })
        .catch(() => {
          this.saveLoading = false
          swal({
            icon: 'error',
            timer: 2000,
            title: 'Failed'
          })
        })
    },
    initChart(domClassName, data, title) {
      if (!domClassName) return false
      var dom = document.getElementById(domClassName)
      let chart = this.echarts.init(dom)
      chart.setOption({
        title: {
          text: title,
          top: 'top',
          left: 'center'
        },
        xAxis: {
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          }
        },
        yAxis: {
          splitLine: {
            lineStyle: {
              type: 'dashed'
            }
          },
          scale: true
        },
        tooltip: {
          formatter: function(param) {
            return `Cluster Number: ${param.data[1]}<br/>Value: ${
              param.data[0]
            }`
          }
        },
        series: [
          {
            symbolSize: 10,
            data,
            type: 'scatter',
            itemStyle: {
              color:
                domClassName === 'sensorClusterChart'
                  ? new this.echarts.graphic.RadialGradient(0.4, 0.3, 1, [
                      {
                        offset: 0,
                        color: 'rgb(251, 118, 123)'
                      },
                      {
                        offset: 1,
                        color: 'rgb(204, 46, 72)'
                      }
                    ])
                  : new this.echarts.graphic.RadialGradient(0.4, 0.3, 1, [
                      {
                        offset: 0,
                        color: 'rgb(129, 227, 238)'
                      },
                      {
                        offset: 1,
                        color: 'rgb(25, 183, 207)'
                      }
                    ])
            }
          }
        ]
      })
      window.addEventListener('resize', function() {
        chart.resize()
      })
      this.$nextTick(() => {
        chart.resize()
      })
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.run-pc-page {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.app-card {
  padding: 20px 24px;
}

.section-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text, #111827);
  padding: 6px 0;
  margin: 12px 0 10px;
  border-bottom: 1px solid var(--border, #e2e5ec);
}

.org-row {
  margin-bottom: 4px;
}

.org-btn-col {
  display: flex;
  align-items: flex-end;
  padding-bottom: 18px;
}

.form-actions {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

.result-grid {
  margin-bottom: 12px;
}

.result-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 0;
}

.result-label {
  font-size: 11px;
  color: var(--text2, #6b7280);
  font-weight: 500;
}

.result-value {
  font-size: 13px;
  font-weight: 600;
  color: var(--blue, #2563eb);
}

.chart-area {
  width: 100%;
  height: 400px;
  margin-top: 12px;
}

/deep/ .el-form-item__label {
  line-height: 1.4;
  padding: 0;
  margin-bottom: 4px;
  font-size: 12px;
  color: var(--text2, #6b7280);
}

/deep/ .el-form-item {
  margin-bottom: 14px;
}
</style>
